import React, { useState, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import Icon from '../../../components/AppIcon';

const ErrorAnalyticsPanel = ({ compact = false, refreshTrigger }) => {
  const [analytics, setAnalytics] = useState({
    errorsByType: [],
    topErrors: [],
    errorTrends: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, refreshTrigger]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      // Calculate date range
      const now = new Date();
      let startDate;
      switch (timeRange) {
        case '1h':
          startDate = new Date(now - 60 * 60 * 1000);
          break;
        case '24h':
          startDate = new Date(now - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now - 24 * 60 * 60 * 1000);
      }

      // Fetch failed events and JWT failures
      const [failedEvents, jwtFailures] = await Promise.all([
        adminService?.getWebhookEvents({
          limit: 1000,
          status: 'failed',
          startDate: startDate?.toISOString()
        }),
        adminService?.getJwtValidationFailures({
          limit: 1000,
          startDate: startDate?.toISOString()
        })
      ]);

      // Analyze errors by type
      const errorTypeMap = {};
      failedEvents?.forEach((event) => {
        const type = event?.eventType || 'Unknown';
        errorTypeMap[type] = (errorTypeMap?.[type] || 0) + 1;
      });

      jwtFailures?.forEach((failure) => {
        const type = `JWT: ${failure?.errorType}`;
        errorTypeMap[type] = (errorTypeMap?.[type] || 0) + 1;
      });

      const errorsByType = Object?.entries(errorTypeMap)
        ?.map(([type, count]) => ({ type, count }))
        ?.sort((a, b) => b?.count - a?.count);

      // Extract top error messages
      const errorMessageMap = {};
      failedEvents?.forEach((event) => {
        if (event?.errorMessage) {
          const msg = event?.errorMessage?.substring(0, 100);
          errorMessageMap[msg] = (errorMessageMap?.[msg] || 0) + 1;
        }
      });

      jwtFailures?.forEach((failure) => {
        if (failure?.errorMessage) {
          const msg = failure?.errorMessage?.substring(0, 100);
          errorMessageMap[msg] = (errorMessageMap?.[msg] || 0) + 1;
        }
      });

      const topErrors = Object?.entries(errorMessageMap)
        ?.map(([message, count]) => ({ message, count }))
        ?.sort((a, b) => b?.count - a?.count)
        ?.slice(0, 5);

      setAnalytics({
        errorsByType,
        topErrors,
        totalErrors: failedEvents?.length + jwtFailures?.length
      });
    } catch (err) {
      console.error('Load error analytics error:', err);
      setError(err?.message || 'Failed to load error analytics');
    } finally {
      setLoading(false);
    }
  };

  const getErrorTypeIcon = (type) => {
    if (type?.includes('checkout')) return 'ShoppingCart';
    if (type?.includes('invoice')) return 'FileText';
    if (type?.includes('subscription')) return 'RefreshCw';
    if (type?.includes('JWT')) return 'ShieldAlert';
    return 'AlertCircle';
  };

  const getErrorTypeColor = (type) => {
    if (type?.includes('JWT')) return 'text-warning';
    if (type?.includes('failed')) return 'text-destructive';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-24 bg-muted rounded"></div>
            <div className="h-24 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
            <Icon name="BarChart3" size={20} className="text-destructive" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Payment Error Analytics</h2>
            <p className="text-sm text-muted-foreground">Aggregated error patterns and common failure reasons</p>
          </div>
        </div>
        {!compact && (
          <div className="flex items-center gap-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
            <button
              onClick={loadAnalytics}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {/* Total Errors Summary */}
      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Total Errors</p>
            <p className="text-3xl font-bold text-foreground">{analytics?.totalErrors || 0}</p>
          </div>
          <div className="w-16 h-16 rounded-lg bg-destructive/10 flex items-center justify-center">
            <Icon name="AlertTriangle" size={32} className="text-destructive" />
          </div>
        </div>
      </div>

      {/* Errors by Type */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Errors by Type</h3>
        <div className="space-y-2">
          {analytics?.errorsByType?.length === 0 ? (
            <div className="text-center py-6">
              <Icon name="CheckCircle" size={32} className="text-success mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No errors found in this time range</p>
            </div>
          ) : (
            analytics?.errorsByType?.slice(0, compact ? 3 : 10)?.map((item, index) => {
              const maxCount = analytics?.errorsByType?.[0]?.count || 1;
              const percentage = (item?.count / maxCount) * 100;
              const icon = getErrorTypeIcon(item?.type);
              const color = getErrorTypeColor(item?.type);

              return (
                <div key={index} className="border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon name={icon} size={16} className={color} />
                      <span className="text-sm font-medium text-foreground">{item?.type}</span>
                    </div>
                    <span className="text-sm font-bold text-foreground">{item?.count}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-destructive rounded-full h-2 transition-all"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Top Error Messages */}
      {!compact && analytics?.topErrors?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Most Common Error Messages</h3>
          <div className="space-y-2">
            {analytics?.topErrors?.map((item, index) => (
              <div key={index} className="border border-border rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-foreground flex-1">{item?.message}</p>
                  <span className="text-sm font-bold text-destructive flex-shrink-0">{item?.count}x</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorAnalyticsPanel;